import isEqual from 'lodash/isEqual';
import React from 'react';
import { Link } from 'react-router-dom';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import MoreIcon from '@atlaskit/icon/glyph/more';
import { cloudSiteNameSearchResponse, Permissions, Roles } from '@atlassian/cssxp-typescommon';
import { useSidebar } from '../../hooks';
import { analytics, createOpenSidebarFromButtonEvent } from '@atlassian/temp-analytics';
import { GloballyAllowedSidebar } from '@atlassian/cssxp-utils-generic';
import { ColGroup, SearchResultsTable, TableBody, TableHead } from '../../search-results-table';
import { OpenSidebarRow } from '../../open-sidebar-row';
import EntitlementIcon from '../../entitlement-icon';
import { hasAtleastOneRole, isAccess } from '../../security';

interface CloudSiteData {
  cloudName: string;
  clousiteId?: string;
  companyName?: string;
  companyDomain?: string;
}

export interface CloudSiteAction {
  id: string;
  content: string;
  onClick: (cloudSiteData: CloudSiteData) => void;
  predicate?: () => boolean;
  isDisabled: () => boolean;
}

export interface ReservedSiteSearchProp {
  cloudName?: string;
  searchAllReservedSites?: boolean;
}

export interface ComponentProps {
  results: cloudSiteNameSearchResponse[] | null;
  isSearching: boolean;
  error: Error | null;
  isLoadMoreEnabled: boolean;
  onLoadMoreClick: () => any;
}

const isActionAuthorized = (action: CloudSiteAction) => action.predicate === undefined || action.predicate() === true;

const getColumns = () => {
  const columns = [
    {
      name: 'Cloud Name',
      customClassNames: 'col-30',
    },
    {
      name: 'Company Name',
      customClassNames: 'col-20',
    },
    {
      name: 'Company Domain',
      customClassNames: 'col-20',
    },
    {
      name: 'Status',
      customClassNames: 'col-20',
    },
    {
      name: '',
      customClassNames: 'col-10',
    },
    {
      name: '',
      customClassNames: '',
    },
  ];
  return columns;
};

export type Props = ComponentProps;

export const ReservedCloudSiteList = (props: Props) => {
  const { error, isSearching, isLoadMoreEnabled, onLoadMoreClick, results = [] } = props;

  const { isSidebarOpen, payload } = useSidebar();
  const showRenameCloudSite =
    isAccess(Permissions.CLOUD_SITE_MANAGE) && hasAtleastOneRole([Roles.COGS_SITE_SUPPORT, Roles.COGS_CLOUD_SUPPORT]);
  const { openSidebar } = useSidebar();
  const hasNoResults = !isSearching && !error && results?.length === 0;

  // eslint-disable-next-line @typescript-eslint/ban-types
  const handleCloudSiteAction = (sidebarType: string, payload?: object) => {
    analytics.sendUIEvent(createOpenSidebarFromButtonEvent(sidebarType));
    openSidebar({ sidebarType, payload });
  };

  const cloudSiteActions: CloudSiteAction[] = [
    {
      id: 'Un-assign',
      content: 'Un-Assign reserved cloud site',
      onClick: (cloudSiteData) =>
        handleCloudSiteAction(GloballyAllowedSidebar.UNASSIGN_CLOUD_SITE_PANE, {
          cloudSiteId: cloudSiteData.clousiteId,
          cloudSiteName: cloudSiteData.cloudName,
        }),
      predicate: () => showRenameCloudSite,
      isDisabled: () => false,
    },
    {
      id: 'update-reserved-sitename',
      content: 'Update',
      onClick: (cloudSiteData) =>
        handleCloudSiteAction(GloballyAllowedSidebar.ADD_NEW_CLOUD_SITE, {
          cloudName: cloudSiteData.cloudName,
          companyName: cloudSiteData.companyName,
          companyDomain: cloudSiteData.companyDomain,
          isUpdate: true,
        }),
      isDisabled: () => false,
    },
  ];

  const authorizedActions = cloudSiteActions.filter((action) => isActionAuthorized(action));

  return (
    <div data-testid="reserved-site-list" style={{ order: hasNoResults ? 1 : 0, marginTop: 30 }}>
      <SearchResultsTable
        id="reservedSiteNameSearch"
        isLoading={isSearching}
        error={error?.toString()}
        heading={'Reserved Cloud Sites'}
        noResults={hasNoResults}
        loadMore={{
          showLoadMore: isLoadMoreEnabled,
          onLoadMoreClick: onLoadMoreClick,
          //   showResultsCount: false,
        }}
        entityName="cloudSites"
        // isScrollable={true}
      >
        <ColGroup columns={getColumns()} />
        <TableHead columns={getColumns()} />
        <TableBody>
          {props.results?.map((reservedSite, index) => {
            const identifier = reservedSite.cloudName;

            const rowPayload = {
              cloudName: identifier ?? null,
              cloudSiteData: reservedSite ?? null,
            };

            const isSelected = isSidebarOpen && isEqual(payload, rowPayload);
            const clousiteId = reservedSite?.ARI ? reservedSite.ARI?.split('/')?.[1] : null;

            return (
              <React.Fragment key={`reserved_cloud_site_${index}`}>
                <OpenSidebarRow
                  sidebar={GloballyAllowedSidebar.CLOUD_SITE_AUDIT_LOG_PREVIEW}
                  payload={rowPayload}
                  isSelected={isSelected}
                >
                  <td>
                    {clousiteId ? (
                      <Link
                        to={`/cloud-sites/${clousiteId}`}
                        onClick={(e) => e.stopPropagation()}
                        data-testid="cloudsite-id"
                        className="avatar-container"
                      >
                        <EntitlementIcon productId="atlassian.cloud" className="avatar medium" isCloud />
                        {reservedSite?.cloudName}
                      </Link>
                    ) : (
                      <div className="avatar-container" style={{ fontWeight: 600 }}>
                        <EntitlementIcon productId="atlassian.cloud" className="avatar medium" isCloud />
                        {reservedSite?.cloudName}
                      </div>
                    )}
                  </td>
                  <td>{reservedSite.companyName ?? 'N/A'}</td>
                  <td>{reservedSite.companyDomain ?? 'N/A'}</td>
                  <td>{reservedSite?.ARI ? 'Assigned' : 'Un-Assigned'}</td>
                  <td className="dropdown-cell" onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu
                      isMenuFixed={true}
                      position="bottom right"
                      triggerType="button"
                      triggerButtonProps={{
                        appearance: 'subtle',
                        iconBefore: <MoreIcon label="More" />,
                        className: 'trigger-button',
                        style: {
                          marginLeft: '4px',
                        },
                        role: 'menu',
                      }}
                    >
                      <DropdownItemGroup>
                        {authorizedActions.map((cloudSiteAction, index) => {
                          return (
                            <DropdownItem
                              key={`action_item_${index}`}
                              onClick={() => {
                                cloudSiteAction.onClick({
                                  cloudName: reservedSite?.cloudName ?? '',
                                  clousiteId: clousiteId ?? '',
                                  companyName: reservedSite?.companyName ?? '',
                                  companyDomain: reservedSite?.companyDomain ?? '',
                                });
                              }}
                              isDisabled={cloudSiteAction?.id === 'Un-assign' && !clousiteId ? true : false}
                            >
                              {cloudSiteAction.content}
                            </DropdownItem>
                          );
                        })}
                      </DropdownItemGroup>
                    </DropdownMenu>
                  </td>
                  <td></td>
                </OpenSidebarRow>
              </React.Fragment>
            );
          })}
        </TableBody>
      </SearchResultsTable>
    </div>
  );
};
